import { QRCodeSVG } from "qrcode.react";
import React, { useEffect, useState } from "react";
import { Download, Smartphone, Share, Plus, X, Apple } from "lucide-react";

// Reusable "Install app" entry point for the marketing site.
// Behavior:
// - If running inside the installed/standalone PWA → render nothing.
// - On Android/Chrome with a captured beforeinstallprompt → native install prompt.
// - On iPhone/iPad Safari → open a bottom sheet with the 3-step Add-to-Home-Screen flow.
// - On desktop → open a modal with a QR code pointing to /m/home for the phone to scan.

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function detectPlatform() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}

export default function InstallAppButton({
  className = "",
  variant = "primary",
  label = "Install app",
  compact = false,
  testId = "install-app-button",
}) {
  const [installed, setInstalled] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [modal, setModal] = useState(null); // "ios" | "desktop" | null

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    setInstalled(isStandalone());
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    const plat = detectPlatform();
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice.catch(() => null);
      setDeferred(null);
      return;
    }
    if (plat === "ios") setModal("ios");
    else if (plat === "android") setModal("desktop"); // same QR fallback if event wasn't captured
    else setModal("desktop");
  };

  const base =
    "inline-flex items-center gap-1.5 transition-colors font-bold tracking-[0.18em] uppercase";
  const sized = compact ? "px-3 py-2 text-[11px]" : "px-5 py-2.5 text-[12px]";
  const theme =
    variant === "ghost"
      ? "bg-transparent border border-[var(--lc-ink)] text-[var(--lc-ink)] hover:bg-[var(--lc-ink)] hover:text-[var(--lc-cream)]"
      : "bg-[var(--lc-moss)] text-white hover:bg-[var(--lc-moss-dark)]";

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`${base} ${sized} ${theme} ${className}`}
        data-testid={testId}
      >
        <Smartphone className="h-3.5 w-3.5" /> {label}
      </button>

      {modal === "ios" && <IOSSheet onClose={() => setModal(null)} />}
      {modal === "desktop" && <DesktopQR onClose={() => setModal(null)} />}
    </>
  );
}

function Backdrop({ onClose, children, align = "end" }) {
  return (
    <div
      className={`fixed inset-0 z-[70] bg-[var(--lc-ink)]/50 backdrop-blur-sm grid ${
        align === "end" ? "place-items-end" : "place-items-center"
      } p-0 sm:p-4`}
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}

function IOSSheet({ onClose }) {
  return (
    <Backdrop onClose={onClose} align="end">
      <div
        className="w-screen sm:w-[480px] bg-white rounded-t-2xl sm:rounded-2xl p-6"
        style={{ paddingBottom: "calc(24px + env(safe-area-inset-bottom))" }}
        data-testid="install-ios-modal"
      >
        <div className="w-10 h-1 rounded-full bg-[var(--lc-line)] mx-auto mb-4 sm:hidden" />
        <div className="flex items-center justify-between gap-3">
          <div className="eyebrow text-[var(--lc-moss)] flex items-center gap-2">
            <Apple className="h-3 w-3" /> On iPhone
          </div>
          <button onClick={onClose} className="text-[var(--lc-muted)] hover:text-[var(--lc-ink)]">
            <X className="h-4 w-4" />
          </button>
        </div>
        <h3 className="font-serif-display text-2xl mt-1">Add LONGEV to your home screen</h3>
        <ol className="mt-5 space-y-4 text-sm">
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 rounded-full bg-[var(--lc-cream)] grid place-items-center flex-shrink-0 border border-[var(--lc-line)]">
              <Share className="h-3.5 w-3.5" />
            </span>
            <span>
              <strong>Open this page in Safari</strong>, then tap the Share icon
              <span className="block text-xs text-[var(--lc-muted)] mt-0.5">
                (the square with an upward arrow at the bottom of the screen)
              </span>
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 rounded-full bg-[var(--lc-cream)] grid place-items-center flex-shrink-0 border border-[var(--lc-line)]">
              <Plus className="h-3.5 w-3.5" />
            </span>
            <span>
              Scroll and tap <strong>"Add to Home Screen"</strong>
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 rounded-full bg-[var(--lc-cream)] grid place-items-center flex-shrink-0 border border-[var(--lc-line)] text-[11px] font-bold">
              3
            </span>
            <span>
              Tap <strong>Add</strong>. LONGEV lives on your home screen like any other app.
            </span>
          </li>
        </ol>
        <button
          onClick={onClose}
          className="mt-6 w-full px-5 py-3.5 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase rounded-md"
          data-testid="install-ios-modal-close"
        >
          Got it
        </button>
      </div>
    </Backdrop>
  );
}

function DesktopQR({ onClose }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    const u = `${window.location.origin}/m/home`;
    setUrl(u);
  }, []);


  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* ignore */
    }
  };

  return (
    <Backdrop onClose={onClose} align="center">
      <div
        className="w-screen sm:w-[460px] bg-white rounded-2xl p-7"
        data-testid="install-desktop-modal"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="eyebrow text-[var(--lc-moss)] flex items-center gap-2">
            <Download className="h-3 w-3" /> Open on your phone
          </div>
          <button onClick={onClose} className="text-[var(--lc-muted)] hover:text-[var(--lc-ink)]">
            <X className="h-4 w-4" />
          </button>
        </div>
        <h3 className="font-serif-display text-2xl mt-1">
          Scan to install <span className="italic">LONGEV.</span>
        </h3>
        <p className="text-xs text-[var(--lc-muted)] mt-2">
          Open your phone camera, scan the code, and follow the one-tap install.
        </p>

        <div className="mt-5 grid place-items-center">
          {url ? (
            <div className="p-3 border border-[var(--lc-line)] rounded-md bg-white" data-testid="install-qr-image">
              <QRCodeSVG value={url} size={200} fgColor="#0E1814" bgColor="#FFFFFF" level="M" title="QR to install LONGEV mobile app" />
            </div>
          ) : (
            <div className="w-56 h-56 bg-[var(--lc-cream)]" />
          )}
        </div>

        <div className="mt-5">
          <div className="eyebrow text-[var(--lc-muted)] mb-1.5">Or share this link</div>
          <div className="flex gap-2">
            <input
              readOnly
              value={url}
              className="flex-1 px-3 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm"
              onFocus={(e) => e.target.select()}
              data-testid="install-desktop-url"
            />
            <button
              onClick={copy}
              className="px-4 py-2.5 bg-[var(--lc-ink)] text-white text-[11px] font-bold tracking-[0.14em] uppercase rounded-sm"
              data-testid="install-desktop-copy"
            >
              Copy
            </button>
          </div>
        </div>

        <p className="mt-4 text-[11px] text-[var(--lc-muted)]">
          Works on iPhone (Safari → Add to Home Screen) and Android (Chrome → Install app). No app
          store needed.
        </p>
      </div>
    </Backdrop>
  );
}
